/**
 * OverviewTab Component
 * Displays event overview information including description, image, settings, and voucher configuration
 */

import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PencilIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import Button from "../../common/Button";
import EventCloseRateCard from "../EventCloseRateCard";
import EventAttachmentsSection from "../EventAttachmentsSection";
import EventProvenanceSection from "../EventProvenanceSection";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useToast } from "../../ui/Toast";
import {
  getEventOrganizations,
  getEventLocations,
  getEventLinks,
  getEventFunnelStats,
  updateEvent,
  saveEventOrgLocations,
} from "../../../services/events.service";
import type { Event } from "../../../types/event.types";
import { US_STATES } from "../../../types/event.types";
import PromoterSelector from "../PromoterSelector";
import EventOrganizationLocationSelector, {
  type EventOrgLocationSelection,
} from "../EventOrganizationLocationSelector";
import { useCanEditEvent } from "../../../hooks/useEventPermissions";
import { useAuth } from "../../../contexts/AuthContext";
import { eventCTAKeys } from "../../../hooks/useEventCTAs";
import { eventHostCTAKeys } from "../EventHostCTAsPanel";
import { eventKeys } from "../../../hooks/useEvents";
import { afterEventLinksSaved } from "../../../services/eventCTAAutoPrepare.service";

// Types for event association responses
interface OrgAssociation {
  id: string;
  organizations?: { id: string; name: string };
}

interface LocAssociation {
  id: string;
  locations?: {
    id: string;
    name: string;
    organization_id: string;
    organizations?: { name: string };
  };
}

interface LinkAssociation {
  id: string;
  url: string;
  label: string;
  description?: string;
  link_type?: string;
}

interface OverviewTabProps {
  event: Event;
  eventId: string;
  googleMapsLink: string | null;
  /** RM/TM (owner 2026-10-29): they view an event's setup but never change it; every Edit control is hidden. */
  readOnly?: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  event,
  eventId,
  googleMapsLink,
  readOnly = false,
}) => {
  // Safe accessor for location address
  const locationAddress =
    event.location_address && typeof event.location_address === "object"
      ? event.location_address
      : null;

  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Fixed 2026-10-03: every inline save here invalidated ["events", eventId], but EventDetail
  // reads the event under eventKeys.detail(id) = ["events", "detail", id]. That prefix never
  // matched, so the page kept the stale event: a saved description still showed "No description
  // added" until a reload. Patch the cached event with what was saved, then refetch BOTH keys
  // (["events", eventId] is still used by the voucher panel).
  const refreshEvent = async (saved?: Partial<Event>) => {
    if (saved) {
      queryClient.setQueryData<Event | undefined>(
        eventKeys.detail(eventId),
        (old) => (old ? { ...old, ...saved } : old),
      );
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) }),
      queryClient.invalidateQueries({ queryKey: ["events", eventId] }),
    ]);
  };
  const { user } = useAuth();

  // Description editing
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState(
    event.description || "",
  );
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // Notes editing
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editNotes, setEditNotes] = useState(event.notes || "");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Settings editing
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [editSettings, setEditSettings] = useState({
    state: event.state || "",
    is_public: event.is_public || false,
    is_featured: event.is_featured || false,
    registration_required: event.registration_required || false,
    max_attendees: event.max_attendees?.toString() || "",
    allow_walk_ins: event.allow_walk_ins || false,
    walk_in_limit: event.walk_in_limit?.toString() || "",
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Promoter editing
  const [isEditingPromoter, setIsEditingPromoter] = useState(false);
  const [editPromoterId, setEditPromoterId] = useState<string | null>(
    (event as any).promoter_id || null,
  );
  const [editPromoterName, setEditPromoterName] = useState<string | null>(
    event.promoter_name || null,
  );
  const [isSavingPromoter, setIsSavingPromoter] = useState(false);

  // Organization/location association editing
  const [isEditingAssociations, setIsEditingAssociations] = useState(false);
  const [associationSelections, setAssociationSelections] = useState<
    EventOrgLocationSelection[]
  >([]);
  const [isSavingAssociations, setIsSavingAssociations] = useState(false);

  // Save description
  const handleSaveDescription = async () => {
    setIsSavingDescription(true);
    try {
      // null, not undefined: undefined is dropped from the PATCH, so clearing never saved.
      const description = editDescription.trim() ? editDescription : null;
      await updateEvent(eventId, {
        description,
      } as unknown as Parameters<typeof updateEvent>[1]);
      await refreshEvent({ description: description ?? undefined });
      showToast("success", "Description saved");
      setIsEditingDescription(false);
    } catch (error) {
      showToast("error", "Failed to save description");
    } finally {
      setIsSavingDescription(false);
    }
  };

  // Save notes
  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      // null, not undefined: undefined is dropped from the PATCH, so clearing never saved.
      const notes = editNotes.trim() ? editNotes : null;
      await updateEvent(eventId, {
        notes,
      } as unknown as Parameters<typeof updateEvent>[1]);
      await refreshEvent({ notes: notes ?? undefined });
      showToast("success", "Notes saved");
      setIsEditingNotes(false);
    } catch (error) {
      showToast("error", "Failed to save notes");
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await updateEvent(eventId, {
        state: editSettings.state || undefined,
        is_public: editSettings.is_public,
        is_featured: editSettings.is_featured,
        registration_required: editSettings.registration_required,
        max_attendees: editSettings.max_attendees
          ? parseInt(editSettings.max_attendees)
          : undefined,
        allow_walk_ins: editSettings.allow_walk_ins,
        walk_in_limit: editSettings.walk_in_limit
          ? parseInt(editSettings.walk_in_limit)
          : undefined,
      });
      void refreshEvent();
      showToast("success", "Settings saved");
      setIsEditingSettings(false);
    } catch (error) {
      showToast("error", "Failed to save settings");
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Save promoter
  const handleSavePromoter = async () => {
    setIsSavingPromoter(true);
    try {
      await updateEvent(eventId, {
        promoter_id: editPromoterId || null,
        promoter_name: editPromoterName || null,
      } as any);
      void refreshEvent();
      showToast("success", "Promoter saved");
      setIsEditingPromoter(false);
    } catch (error) {
      showToast("error", "Failed to save promoter");
    } finally {
      setIsSavingPromoter(false);
    }
  };

  // Edit capability — the SAME decision used everywhere else for event edits
  // (CallToActionTab, SharingTab, MsrEventDetail): superadmin/admin, an owner/edit
  // grant, or the assigned MSR. Read-only roles (e.g. support_agent) get canEdit=false
  // so they never see the association edit control. The server RPC stays the real
  // boundary; this just keeps the UI from offering an action that would be refused.
  const { canEdit } = useCanEditEvent(eventId);

  // Fetch associated organizations and locations
  const orgsQuery = useQuery({
    queryKey: ["event-organizations", eventId],
    queryFn: () => getEventOrganizations(eventId),
  });
  const orgsData = orgsQuery.data;

  const locsQuery = useQuery({
    queryKey: ["event-locations", eventId],
    queryFn: () => getEventLocations(eventId),
  });
  const locsData = locsQuery.data;

  // Association load state. The edit control MUST NOT appear until BOTH queries have
  // loaded successfully: opening the editor seeds the selector from these lists, so a
  // click while they are still undefined would seed an EMPTY selection and Save would
  // then wipe valid associations (external audit BLOCKER — edit-before-load).
  const associationsLoading = orgsQuery.isLoading || locsQuery.isLoading;
  const associationsError = orgsQuery.isError || locsQuery.isError;
  const associationsLoaded = orgsQuery.isSuccess && locsQuery.isSuccess;

  const { data: linksData } = useQuery({
    queryKey: ["event-links", eventId],
    queryFn: () => getEventLinks(eventId),
  });

  // Fetch funnel stats
  const { data: funnelStats } = useQuery({
    queryKey: ["event-funnel", eventId],
    queryFn: () => getEventFunnelStats(eventId),
  });

  const hasAssociations =
    (orgsData && orgsData.length > 0) || (locsData && locsData.length > 0);

  // Enter association edit mode, seeding the selector from the currently
  // loaded org/location associations. Mirrors EventForm's mapping of the
  // event_organizations / event_locations rows into the selector's shape.
  const handleEditAssociations = () => {
    // Defensive guard: never enter edit mode before both association lists have
    // loaded, or we would seed the selector from undefined and Save would clear
    // valid associations. The trigger button is already gated on the same flag.
    if (!associationsLoaded) {
      return;
    }
    const selections: EventOrgLocationSelection[] = [];
    (orgsData as OrgAssociation[] | undefined)?.forEach((assoc) => {
      if (assoc.organizations) {
        selections.push({
          org_id: assoc.organizations.id,
          org_name: assoc.organizations.name,
          location_id: null,
        });
      }
    });
    (locsData as LocAssociation[] | undefined)?.forEach((assoc) => {
      if (assoc.locations && assoc.locations.organizations) {
        selections.push({
          org_id: assoc.locations.organization_id,
          org_name: assoc.locations.organizations.name,
          location_id: assoc.locations.id,
          location_name: assoc.locations.name,
        });
      }
    });
    setAssociationSelections(selections);
    setIsEditingAssociations(true);
  };

  // Save the org/location associations. saveEventOrgLocations clears + rewrites
  // both event_organizations and event_locations, then we invalidate the two
  // queries that feed the display so it refreshes.
  const handleSaveAssociations = async () => {
    // Saving an empty selection removes ALL org/location associations. Confirm first
    // so an accidental "open editor, immediately Save" can't silently wipe them
    // (external audit BLOCKER — empty-save data loss).
    if (associationSelections.length === 0) {
      const confirmed = window.confirm(
        "Remove ALL organization and location associations from this event?",
      );
      if (!confirmed) {
        return;
      }
    }
    setIsSavingAssociations(true);
    try {
      await saveEventOrgLocations(eventId, associationSelections);
      // Linking the promoter / host prepares the generic CTA (database, at commit); draw its QR and
      // save its flyer before the save finishes, then refresh the CTA and Files tabs. Never throws.
      if (user?.id) {
        const { qr } = await afterEventLinksSaved(eventId, user.id);
        if (qr) {
          queryClient.invalidateQueries({
            queryKey: eventCTAKeys.list(eventId),
          });
          queryClient.invalidateQueries({
            queryKey: ["event-cta-init", eventId],
          });
          queryClient.invalidateQueries({
            queryKey: ["event-cta-flyer", eventId],
          });
          queryClient.invalidateQueries({
            queryKey: ["event-attachments", eventId],
          });
        }
      }
      queryClient.invalidateQueries({
        queryKey: ["event-organizations", eventId],
      });
      // the venue / host CTAs a class, table, seminar or speaking engagement shows (and its tab badge)
      queryClient.invalidateQueries({
        queryKey: eventHostCTAKeys.list(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventHostCTAKeys.orgs(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: ["event-locations", eventId],
      });
      showToast("success", "Organization association saved");
      setIsEditingAssociations(false);
    } catch (error) {
      showToast("error", "Failed to save organization association");
    } finally {
      setIsSavingAssociations(false);
    }
  };

  // Helper functions for link types
  const getLinkTypeColor = (type: string) => {
    switch (type) {
      case "signup":
        return "bg-green-50 border-green-200 text-green-800";
      case "marketing":
        return "bg-purple-50 border-purple-200 text-purple-800";
      case "website":
        return "bg-blue-50 border-blue-200 text-blue-800";
      case "social":
        return "bg-pink-50 border-pink-200 text-pink-800";
      case "document":
        return "bg-yellow-50 border-yellow-200 text-yellow-800";
      default:
        return "bg-gray-50 border-gray-200 text-gray-800";
    }
  };

  const getLinkTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      signup: "Signup Form",
      marketing: "Marketing",
      website: "Website",
      social: "Social Media",
      document: "Document",
      external: "External Link",
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Event Funnel Stats */}
      {funnelStats &&
        (funnelStats.invited > 0 ||
          funnelStats.registered > 0 ||
          funnelStats.attended > 0) && (
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <ChartBarIcon className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Event Funnel
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
              {/* Invited — PEOPLE. The subtitle used to read "emails sent" under a number that
                  was, in fact, a message count: one person sent an invite and two reminders was
                  displayed as 3 Invited. People and messages are now separate figures, and the
                  message breakdown is shown underneath so nothing is hidden. */}
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <div className="text-3xl font-bold text-gray-900">
                  {funnelStats.invited}
                </div>
                <div className="text-sm text-gray-600">Invited</div>
                <div className="text-xs text-gray-400">
                  {funnelStats.invited === 1 ? "person" : "people"}
                </div>
                {funnelStats.totalCampaignMessages > 0 && (
                  <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500">
                    <div>
                      {funnelStats.totalCampaignMessages}{" "}
                      {funnelStats.totalCampaignMessages === 1
                        ? "message"
                        : "messages"}{" "}
                      sent
                    </div>
                    {/* The breakdown must ADD UP to the total above it. Invite + reminder are not
                        the only flights an event's campaigns can carry, so any remainder is shown
                        as "other" rather than left to silently not reconcile. */}
                    <div className="text-gray-400">
                      {funnelStats.initialInviteMessages} invite
                      {funnelStats.reminderMessages > 0 && (
                        <> · {funnelStats.reminderMessages} reminder</>
                      )}
                      {funnelStats.totalCampaignMessages -
                        funnelStats.initialInviteMessages -
                        funnelStats.reminderMessages >
                        0 && (
                        <>
                          {" "}
                          ·{" "}
                          {funnelStats.totalCampaignMessages -
                            funnelStats.initialInviteMessages -
                            funnelStats.reminderMessages}{" "}
                          other
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="hidden md:flex justify-center">
                <div className="text-gray-300 text-2xl">→</div>
              </div>

              {/* Registered */}
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <div className="text-3xl font-bold text-blue-600">
                  {funnelStats.registered}
                </div>
                <div className="text-sm text-gray-600">Registered</div>
                <div className="text-xs text-gray-400">
                  {funnelStats.registeredHeadcount} people, in advance
                </div>
                {/* people ÷ people. This divided registered PEOPLE by a MESSAGE count, so an
                    event with reminders reported a conversion rate far below the real one. */}
                {funnelStats.invited > 0 && (
                  <div className="text-xs text-blue-500">
                    {Math.round(
                      (funnelStats.registered / funnelStats.invited) * 100,
                    )}
                    % of people invited
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="hidden md:flex justify-center">
                <div className="text-gray-300 text-2xl">→</div>
              </div>

              {/* Attended. CANONICAL total = advance check-ins + walk-ins (see
                  utils/eventAttendance). This used to print the resolver's
                  attended_headcount, which excludes walk-ins by design, so the Overview
                  and the Attendees tab reported different numbers for the same event. The
                  walk-in half stays visible as its own line rather than disappearing into
                  the total. The show rate deliberately stays ADVANCE-ONLY on both sides:
                  walk-ins never registered, so counting them would push it past 100% and
                  stop it meaning "how many of the people we expected turned up". */}
              <div className="text-center p-4 bg-white rounded-lg shadow-sm border-2 border-green-200">
                <div className="text-3xl font-bold text-green-600">
                  {funnelStats.attended}
                </div>
                <div className="text-sm text-gray-600">Attended</div>
                <div className="text-xs text-gray-400">
                  {funnelStats.attendedAdvance} registered +{" "}
                  {funnelStats.attendedWalkIn} walk-in
                </div>
                {funnelStats.advanceShowRate !== null && (
                  <div className="text-xs text-green-500">
                    {Math.round(funnelStats.advanceShowRate * 100)}% advance
                    show rate
                  </div>
                )}
              </div>
            </div>

            {/* Additional Stats Row */}
            {funnelStats.cancelled > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex gap-6 justify-center">
                {funnelStats.cancelled > 0 && (
                  <div className="text-center">
                    <span className="text-lg font-semibold text-red-600">
                      {funnelStats.cancelled}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">
                      Cancelled
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      {/* Description - Always show, editable */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Description</h3>
          {!readOnly && !isEditingDescription && (
            <button
              onClick={() => {
                setEditDescription(event.description || "");
                setIsEditingDescription(true);
              }}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>
        {isEditingDescription ? (
          <div className="space-y-3">
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter event description..."
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditingDescription(false)}
                disabled={isSavingDescription}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveDescription}
                disabled={isSavingDescription}
              >
                {isSavingDescription ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-gray-700 whitespace-pre-wrap">
            {event.description || (
              <span className="text-gray-400 italic">No description added</span>
            )}
          </p>
        )}
      </div>

      {/* Promoter or Sponsoring Organization */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Promoter or Sponsoring Organization
          </h3>
          {!readOnly && !isEditingPromoter && (
            <button
              onClick={() => setIsEditingPromoter(true)}
              className="text-gray-400 hover:text-gray-600"
              title="Edit promoter"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {isEditingPromoter ? (
          <div className="space-y-3">
            <PromoterSelector
              value={editPromoterId}
              onChange={(promoterId, promoterName) => {
                setEditPromoterId(promoterId);
                setEditPromoterName(promoterName);
              }}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSavePromoter}
                disabled={isSavingPromoter}
              >
                {isSavingPromoter ? "Saving..." : "Save"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setIsEditingPromoter(false);
                  setEditPromoterId((event as any).promoter_id || null);
                  setEditPromoterName(event.promoter_name || null);
                }}
                disabled={isSavingPromoter}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : event.promoter_name ? (
          <span className="inline-flex items-center gap-2 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm">
            <BuildingOfficeIcon className="h-4 w-4" />
            <span>{event.promoter_name}</span>
          </span>
        ) : (
          <p className="text-gray-500 text-sm italic">
            No promoter selected. Click the edit icon to add one.
          </p>
        )}
      </div>

      {/* Organization or Location Associations - Hidden for Show events */}
      {/* Show org/location associations for all event types except Show (which uses Promoter instead).
          Rendered even with zero associations so an operator can ADD one via the edit control. */}
      {event.type !== "show" ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Promoter or Sponsoring Organization or Location
            </h3>
            {/* Edit control appears only for users who CAN edit (server RLS/RPC is the
                real boundary) AND only once both association lists have loaded — so a
                click can never seed an empty selection over unloaded data. */}
            {!readOnly &&
              canEdit &&
              associationsLoaded &&
              !isEditingAssociations && (
                <button
                  onClick={handleEditAssociations}
                  className="text-gray-400 hover:text-gray-600"
                  title="Edit organization or location"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
          </div>

          {isEditingAssociations ? (
            <div className="space-y-3">
              <EventOrganizationLocationSelector
                selected={associationSelections}
                onChange={setAssociationSelections}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveAssociations}
                  disabled={isSavingAssociations}
                >
                  {isSavingAssociations ? "Saving..." : "Save"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setIsEditingAssociations(false)}
                  disabled={isSavingAssociations}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : associationsLoading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <LoadingSpinner size="sm" />
              <span>Loading associations...</span>
            </div>
          ) : associationsError ? (
            <p className="text-red-600 text-sm">
              Could not load organization or location associations. Refresh to
              try again.
            </p>
          ) : hasAssociations ? (
            <div className="space-y-3">
              {/* Organizations (all locations) */}
              {orgsData && orgsData.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">
                    Organizations
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(orgsData as OrgAssociation[]).map((assoc) => (
                      <span
                        key={assoc.id}
                        className="inline-flex items-center gap-2 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm"
                      >
                        <BuildingOfficeIcon className="h-4 w-4" />
                        <span>{assoc.organizations?.name}</span>
                        <span className="text-xs text-primary-600">
                          (All locations)
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Specific Locations */}
              {locsData && locsData.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">
                    Specific Locations
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(locsData as LocAssociation[]).map((assoc) => (
                      <span
                        key={assoc.id}
                        className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        <MapPinIcon className="h-4 w-4" />
                        <span className="font-medium">
                          {assoc.locations?.organizations?.name}
                        </span>
                        <span className="text-blue-600">-</span>
                        <span>{assoc.locations?.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm italic">
              No organization or location associated.
              {canEdit ? " Click the edit icon to add one." : ""}
            </p>
          )}
        </div>
      ) : null}

      {/* External Links */}
      {linksData && linksData.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            External Links
          </h3>
          <div className="space-y-3">
            {(linksData as LinkAssociation[]).map((link) => (
              <div
                key={link.id}
                className={`p-3 rounded-lg border ${getLinkTypeColor(link.link_type)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <LinkIcon className="h-4 w-4" />
                      <span className="font-medium">{link.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white border">
                        {getLinkTypeLabel(link.link_type)}
                      </span>
                    </div>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:underline block truncate"
                    >
                      {link.url}
                    </a>
                    {link.description && (
                      <p className="text-sm mt-1 opacity-75">
                        {link.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agenda */}
      {event.agenda && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Agenda</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{event.agenda}</p>
        </div>
      )}

      {/* Class/Training Info */}
      {event.is_class_or_training && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Training Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {event.instructor_name && (
              <div>
                <p className="text-sm text-gray-500">Instructor</p>
                <p className="text-base font-medium text-gray-900">
                  {event.instructor_name}
                </p>
              </div>
            )}
            {event.instructor_contact && (
              <div>
                <p className="text-sm text-gray-500">Contact</p>
                <p className="text-base font-medium text-gray-900">
                  {event.instructor_contact}
                </p>
              </div>
            )}
            {event.registration_fee !== undefined &&
              event.registration_fee > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Registration Fee</p>
                  <p className="text-base font-medium text-gray-900">
                    ${event.registration_fee.toFixed(2)}
                  </p>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Capacity */}
      {event.max_attendees && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Capacity</h3>
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-700">
              {event.current_attendees_count || 0} / {event.max_attendees}{" "}
              registered
            </p>
            {(() => {
              const percentage =
                ((event.current_attendees_count || 0) / event.max_attendees) *
                100;
              if (percentage >= 100) {
                return (
                  <span className="flex items-center gap-1 text-red-600 text-sm font-medium">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    At capacity
                  </span>
                );
              } else if (percentage >= 90) {
                return (
                  <span className="flex items-center gap-1 text-orange-600 text-sm font-medium">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    Nearly full
                  </span>
                );
              }
              return null;
            })()}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                (event.current_attendees_count || 0) / event.max_attendees >= 1
                  ? "bg-red-600"
                  : (event.current_attendees_count || 0) /
                        event.max_attendees >=
                      0.9
                    ? "bg-orange-500"
                    : "bg-primary-600"
              }`}
              style={{
                width: `${Math.min(100, ((event.current_attendees_count || 0) / event.max_attendees) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Settings Section - Editable */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
            Event Settings
          </h3>
          {!readOnly && !isEditingSettings && (
            <button
              onClick={() => {
                setEditSettings({
                  state: event.state || "",
                  is_public: event.is_public || false,
                  is_featured: event.is_featured || false,
                  registration_required: event.registration_required || false,
                  max_attendees: event.max_attendees?.toString() || "",
                  allow_walk_ins: event.allow_walk_ins || false,
                  walk_in_limit: event.walk_in_limit?.toString() || "",
                });
                setIsEditingSettings(true);
              }}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>
        {isEditingSettings ? (
          <div className="space-y-4">
            {/* Regulatory State */}
            <div className="w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regulatory State
              </label>
              <select
                value={editSettings.state}
                onChange={(e) =>
                  setEditSettings((prev) => ({
                    ...prev,
                    state: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select state...</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Used for insurance/regulatory purposes and CTA URLs (ste
                parameter)
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editSettings.is_public}
                  onChange={(e) =>
                    setEditSettings((prev) => ({
                      ...prev,
                      is_public: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Public Event
                </span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editSettings.is_featured}
                  onChange={(e) =>
                    setEditSettings((prev) => ({
                      ...prev,
                      is_featured: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Featured Event
                </span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editSettings.registration_required}
                  onChange={(e) =>
                    setEditSettings((prev) => ({
                      ...prev,
                      registration_required: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Registration Required
                </span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editSettings.allow_walk_ins}
                  onChange={(e) =>
                    setEditSettings((prev) => ({
                      ...prev,
                      allow_walk_ins: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Allow Walk-ins
                </span>
              </label>
            </div>
            {/* The label alone over-promises: this setting does not restrict staff. */}
            <p className="text-xs text-gray-500 -mt-2">
              Controls the public self check-in kiosk only. Staff can always
              walk someone in from the Check-in tab, whichever way this is set.
            </p>
            {editSettings.registration_required && (
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Attendees
                </label>
                <input
                  type="number"
                  value={editSettings.max_attendees}
                  onChange={(e) =>
                    setEditSettings((prev) => ({
                      ...prev,
                      max_attendees: e.target.value,
                    }))
                  }
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
            {editSettings.allow_walk_ins && (
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Walk-in Limit
                </label>
                <input
                  type="number"
                  value={editSettings.walk_in_limit}
                  onChange={(e) =>
                    setEditSettings((prev) => ({
                      ...prev,
                      walk_in_limit: e.target.value,
                    }))
                  }
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditingSettings(false)}
                disabled={isSavingSettings}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveSettings}
                disabled={isSavingSettings}
              >
                {isSavingSettings ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {event.state && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800">
                Regulatory State: {event.state}
              </span>
            )}
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${event.is_public ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}
            >
              {event.is_public ? (
                <CheckCircleIconSolid className="h-4 w-4 mr-1" />
              ) : null}
              Public Event: {event.is_public ? "Yes" : "No"}
            </span>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${event.is_featured ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600"}`}
            >
              {event.is_featured ? (
                <CheckCircleIconSolid className="h-4 w-4 mr-1" />
              ) : null}
              Featured: {event.is_featured ? "Yes" : "No"}
            </span>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${event.registration_required ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"}`}
            >
              Registration:{" "}
              {event.registration_required ? "Required" : "Not Required"}
            </span>
            {event.max_attendees && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                Max: {event.max_attendees} attendees
              </span>
            )}
            {/* This badge used to read "Walk-ins: Not Allowed", which was simply untrue: the flag
                gates only PUBLIC self check-in. Staff have always been able to walk someone in from
                the Check-in tab, and routinely do (42 operator walk-ins on "not allowed" events in
                PROD since 2026-06-01). Say what the flag actually controls. */}
            <span
              title={
                event.allow_walk_ins
                  ? "Visitors can register themselves as walk-ins at the self check-in kiosk, and staff can walk someone in from the Check-in tab."
                  : "The self check-in kiosk will not register new walk-ins for this event. Staff can still walk someone in from the Check-in tab."
              }
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${event.allow_walk_ins ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-600"}`}
            >
              Walk-ins: {event.allow_walk_ins ? "Allowed" : "Staff only"}
              {event.allow_walk_ins &&
                event.walk_in_limit &&
                ` (Limit: ${event.walk_in_limit})`}
            </span>
          </div>
        )}
      </div>

      {/* Notes - Always show, editable */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Internal Notes
          </h3>
          {!readOnly && !isEditingNotes && (
            <button
              onClick={() => {
                setEditNotes(event.notes || "");
                setIsEditingNotes(true);
              }}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>
        {isEditingNotes ? (
          <div className="space-y-3">
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Internal notes (not visible to attendees)..."
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditingNotes(false)}
                disabled={isSavingNotes}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveNotes}
                disabled={isSavingNotes}
              >
                {isSavingNotes ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-gray-700 whitespace-pre-wrap">
            {event.notes || (
              <span className="text-gray-400 italic">No internal notes</span>
            )}
          </p>
        )}
      </div>

      {/* Location address summary + Open-in-Maps link. Smart Admin
          replacement R1 (2026-05-17): the embedded Google Maps iframe
          that used to render here was driven by a hardcoded demo/expired
          API key and showed a default world view instead of the event's
          location. Removed for R1 per audit verdict (option C) — the
          fast, reliable fix is a clickable Open-in-Maps button. A later
          release can swap in Leaflet (already in the bundle for the
          org map) once events reliably have lat/lng so we don't pay
          per-render geocoding cost.

          Same hardcoded key still exists in OrganizationDetail.tsx and
          EventLandingPage.tsx — see memory todo_hardcoded_google_maps_key. */}
      {googleMapsLink &&
        locationAddress &&
        event.location_type !== "virtual" && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Location
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              {[
                locationAddress.address_line1,
                locationAddress.city,
                locationAddress.state,
                locationAddress.postal_code,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
            <a
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Open in Maps
            </a>
          </div>
        )}

      {/* Smart Admin replacement R3 (2026-05-17): Attendance & Close Rate.
          Inline card per audit Decision 7 (KPI, not a workflow tab).
          Shown for all event types per Decision 5; operator decides
          which events warrant tracking. */}
      <EventCloseRateCard event={event} />

      <EventAttachmentsSection eventId={eventId} readOnly={readOnly} />
      {/* Creation/configuration provenance for internal audit. Read-only and
          deliberately last: it is reference information, not a headline. */}
      <EventProvenanceSection eventId={eventId} />
    </div>
  );
};

export default OverviewTab;
